import React, { useState, useRef, useEffect, useCallback } from 'react';
import './css/ChatBot.css';

interface Message {
    text: string[];
    isBot: boolean;
    timestamp: string;
}

const API_URL = "/chatbot.php";

const ChatBot: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<Message[]>([
        {
            text: ["Сайн байна уу! Crown Residence орон сууцны төслийн мэдээллийн ажилтан таньд мэдээлэл хүргэхэд бэлэн байна.", "Танд юугаар туслах вэ?"],
            isBot: true,
            timestamp: new Date().toLocaleTimeString('mn-MN', { hour: '2-digit', minute: '2-digit' })
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const chatContainerRef = useRef<HTMLDivElement | null>(null);

    const suggestedQuestions = [
        "1м²-ийн үнэ хэд вэ?",
        "Байршил нь хаана байдаг вэ?",
        "Хэзээ ашиглалтанд орох вэ?",
        "Холбоо барих утасны дугаар?",
        "Өрөөний сонголт",
        "Гражын төлбөрийн нөхцөл",
        "Урамшуулал",
        "Давуу талууд",
    ];
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (chatContainerRef.current &&
                !chatContainerRef.current.contains(event.target as Node) &&
                !(event.target as Element).closest('.crown-chat-button')) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const sendMessageToAPI = useCallback(async (message: string): Promise<string> => {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

            const response = await fetch(API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    message: message,
                    conversation_history: messages.map(msg => ({
                        user: msg.text.join(' '),
                        bot: msg.isBot ? msg.text.join(' ') : ''
                    })).filter(msg => msg.user && msg.bot)
                }),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                if (response.status === 429) {
                    throw new Error('RATE_LIMIT');
                }
                throw new Error(`API request failed: ${response.status}`);
            }

            const data = await response.json();

            if (data.error) {
                throw new Error(data.error);
            }

            return data.response;
        } catch (error: any) {
            console.error('Error sending message to API:', error);

            if (error.name === 'AbortError') {
                return "Холболт удаан хугацааны дараа тасарлаа. Дахин оролдоно уу.";
            }

            if (error.message === 'RATE_LIMIT') {
                return "Та хэт олон хүсэлт илгээсэн байна. Түр хүлээгээд дахин оролдоно уу.";
            }

            return "Уучлаарай, сервертэй холбогдож чадахгүй байна. Дараа дахин оролдоно уу эсвэл 99571535, 99359230, 88105452 дугаарт холбогдоно уу.";
        }
    }, [messages]);

    const handleSendMessage = useCallback(async () => {
        if (inputValue.trim() === '' || isLoading) return;

        const userMessage = inputValue.trim();
        const newUserMessage: Message = {
            text: [userMessage],
            isBot: false,
            timestamp: new Date().toLocaleTimeString('mn-MN', { hour: '2-digit', minute: '2-digit' })
        };

        setMessages(prev => [...prev, newUserMessage]);
        setInputValue('');
        setIsLoading(true);

        try {
            const botResponseText = await sendMessageToAPI(userMessage);

            const botResponse: Message = {
                text: botResponseText.split('\n').filter(line => line.trim() !== ''),
                isBot: true,
                timestamp: new Date().toLocaleTimeString('mn-MN', { hour: '2-digit', minute: '2-digit' })
            };

            setMessages(prev => [...prev, botResponse]);
        } catch (error) {
            const errorResponse: Message = {
                text: ["Уучлаарай, алдаа гарлаа. Дараа дахин оролдоно уу эсвэл 99571535, 99359230, 88105452 дугаарт холбогдоно уу."],
                isBot: true,
                timestamp: new Date().toLocaleTimeString('mn-MN', { hour: '2-digit', minute: '2-digit' })
            };
            setMessages(prev => [...prev, errorResponse]);
        } finally {
            setIsLoading(false);
        }
    }, [inputValue, isLoading, sendMessageToAPI]);

    const handleSuggestedQuestionClick = useCallback(async (question: string) => {
        if (isLoading) return;

        const newUserMessage: Message = {
            text: [question],
            isBot: false,
            timestamp: new Date().toLocaleTimeString('mn-MN', { hour: '2-digit', minute: '2-digit' })
        };

        setMessages(prev => [...prev, newUserMessage]);
        setIsLoading(true);

        try {
            const botResponseText = await sendMessageToAPI(question);

            const botResponse: Message = {
                text: botResponseText.split('\n').filter(line => line.trim() !== ''),
                isBot: true,
                timestamp: new Date().toLocaleTimeString('mn-MN', { hour: '2-digit', minute: '2-digit' })
            };

            setMessages(prev => [...prev, botResponse]);
        } catch (error) {
            const errorResponse: Message = {
                text: ["Уучлаарай, алдаа гарлаа. Дараа дахин оролдоно уу."],
                isBot: true,
                timestamp: new Date().toLocaleTimeString('mn-MN', { hour: '2-digit', minute: '2-digit' })
            };
            setMessages(prev => [...prev, errorResponse]);
        } finally {
            setIsLoading(false);
        }
    }, [isLoading, sendMessageToAPI]);

    const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !isLoading) {
            handleSendMessage();
        }
    }, [isLoading, handleSendMessage]);

    const clearChat = useCallback(() => {
        setMessages([{
            text: ["Сайн байна уу! Crown Residence орон сууцны төслийн мэдээллийн ажилтан таньд мэдээлэл хүргэхэд бэлэн байна.", "Танд юугаар туслах вэ?"],
            isBot: true,
            timestamp: new Date().toLocaleTimeString('mn-MN', { hour: '2-digit', minute: '2-digit' })
        }]);
    }, []);

    return (
        <div className="crown-chat-widget">
            <div className="crown-chat-button" onClick={() => setIsOpen(true)}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 9h12v2H6V9zm8 5H6v-2h8v2zm4-6H6V6h12v2z"></path>
                </svg>
                {messages.length > 1 && <div className="crown-notification-dot"></div>}
            </div>

            <div
                ref={chatContainerRef}
                className={`crown-chat-container ${isOpen ? 'open' : ''}`}
            >
                <div className="crown-chat-header">
                    <div className="crown-header-content">
                        <h1>Crown Residence</h1>
                        <p>Тав тухтай амьдралын эхлэл</p>
                    </div>
                    <div className="crown-header-actions">
                        <button className="crown-clear-button" onClick={clearChat} title="Хэлэлцүүлэг цэвэрлэх">
                            ⎚
                        </button>
                        <button className="crown-close-button" onClick={() => setIsOpen(false)}>×</button>
                    </div>
                </div>

                <div className="crown-chat-messages">
                    {messages.map((message, index) => (
                        <div key={index} className={`crown-message ${message.isBot ? 'crown-bot-message' : 'crown-user-message'}`}>
                            <div className="crown-message-content">
                                {message.text.map((text, i) => (
                                    <p key={i}>{text}</p>
                                ))}
                            </div>
                            <div className="crown-timestamp">{message.timestamp}</div>
                        </div>
                    ))}

                    {isLoading && (
                        <div className="crown-message crown-bot-message">
                            <div className="crown-typing-indicator">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                    )}

                    <div className="crown-suggested-questions">
                        <div className="crown-suggestions-title">Түгээмэл асуултууд:</div>
                        <div className="crown-suggested-questions-grid">
                            {suggestedQuestions.map((question, index) => (
                                <div
                                    key={index}
                                    className="crown-question-chip"
                                    onClick={() => handleSuggestedQuestionClick(question)}
                                >
                                    {question}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div ref={messagesEndRef} />
                </div>

                <div className="crown-chat-input-container">
                    <input
                        type="text"
                        className="crown-chat-input"
                        placeholder="Crown Residence орон сууцны талаар асуух асуултаа бичнэ үү..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={isLoading}
                        maxLength={1000}
                    />
                    <button
                        className="crown-send-button"
                        onClick={handleSendMessage}
                        disabled={isLoading || inputValue.trim() === ''}
                    >
                        {isLoading ? (
                            <div className="crown-loading-spinner"></div>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path>
                            </svg>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default React.memo(ChatBot);